import { cn } from '@virginexperiencedays/tailwind';

import { LinkWrap } from '../../../../navigation/LinkWrap';
import { IconProductEmbedLogo } from '../../../../icons/IconProductEmbedLogo';
import { Image } from '../../../../layout/Image';

import { CategoryLink } from './CategoryLink';
import { ContentBody } from './ContentBody';
import type { ProductEmbedType } from '..';

type ProductsProps = {
  isSingle: boolean;
  filteredProducts: ProductEmbedType[];
};

export const Products = ({ isSingle, filteredProducts }: ProductsProps) => {
  return (
    <div
      className={cn(
        'my-6 gap-4 self-center',
        isSingle ? 'block flex-row md:flex-col' : 'flex flex-col md:flex-row'
      )}
    >
      {/* non-text product embeds are limited to 2 */}
      {filteredProducts.slice(0, 2).map((product: ProductEmbedType, key) => {
        const { title_override, title, price, url, media, description, categoryHierarchy } =
          product;
        const { mainImage } = media ?? {};
        // @TODO: This should be removed once we retrieve plain string from the API or ideally an array of strings, where each element in the array represents a paragraph.
        const paragraphs: string[] = description ? description.split('</p>') : [];
        const firstParagraph = paragraphs[0] ? paragraphs[0].replace('<p>', '') : description;

        const contentKey = `content-${key}`;
        return (
          <div
            key={key}
            className={cn(
              'text-neutral-strong md:max-h-content flex rounded-lg md:shadow-md',
              isSingle
                ? 'flex-col shadow-md md:m-auto md:max-w-[688px] md:flex-row'
                : 'flex-row items-center md:mx-auto md:my-4 md:max-w-[336px] md:flex-col'
            )}
          >
            <LinkWrap
              className={cn(
                'relative w-full md:max-w-full',
                isSingle ? 'max-w-auto md:w-2/5' : 'max-w-[78px] md:w-full'
              )}
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              <Image
                className={cn(
                  'image:object-cover',
                  isSingle
                    ? 'image:rounded-t-lg h-[222px] md:h-[207px] md:min-w-[203px] md:rounded-l-lg'
                    : 'image:rounded h-[78px] md:h-[222px] md:min-w-[78px] md:rounded-t-lg'
                )}
                src={mainImage?.url}
                alt={title}
                fill
                sizes={
                  isSingle
                    ? '(min-width: 1920px) 10vw, (min-width: 768px) 25vw, 90vw'
                    : '(min-width: 1920px) 15vw, (min-width: 768px) 25vw, 15vw'
                }
              />
              <IconProductEmbedLogo
                className={cn(
                  'absolute right-0 top-0 rounded-tr-lg',
                  !isSingle && 'hidden md:block md:rounded-tr-lg'
                )}
                width="80px"
                height="40px"
              />
            </LinkWrap>
            <div
              className={cn(
                'flex h-full flex-1 flex-col flex-nowrap self-start md:px-4 md:py-6',
                isSingle ? 'p-6' : 'pl-4'
              )}
            >
              {categoryHierarchy && (
                <CategoryLink categoryHierarchy={categoryHierarchy} keySuffix={key} />
              )}
              <ContentBody
                title={title}
                title_override={title_override}
                price={price}
                firstParagraph={firstParagraph}
                contentKey={contentKey}
                isSingle={isSingle}
                url={url}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
